import { makeAutoObservable, runInAction } from "mobx";
import type { AnalyticsManager } from "services/analytics/analyticsManager";
import type { DirectorStore } from "services/director/directorStore";
import type { DataStorage } from "services/storage/dataStorage";
import { type IComplianceCallClient } from "./complianceCallClient";
import { ComplianceCallStatus, isTerminalComplianceCallStatus } from "./complianceCallTypes";

const RETRY_AT_KEY = "final-call-retry-at";
const POLL_INTERVAL_MS = 3_000;
const RETRY_WINDOW_MS = 15 * 1000;

export class ComplianceCallStore {
  status: ComplianceCallStatus = ComplianceCallStatus.NotStarted;
  // Defaults to true so nothing is skipped before the first read comes back.
  isRequired = true;
  error = false;
  isRequesting = false;
  secondsLeft = 0;

  private _pollInterval?: ReturnType<typeof setInterval>;
  private _countdownInterval?: ReturnType<typeof setInterval>;
  // The step has a back button and, in dev, StrictMode mounts it twice against this same instance,
  // so requests from a previous life can still be in flight. Every run captures the epoch it belongs
  // to; init() and dispose() move it on, and a run whose epoch has passed stops touching anything.
  private _epoch = 0;
  // A retry starts a new polling session without disposing the store, so the epoch alone doesn't
  // change — this does, on every _startPolling() call, and a poll whose generation has been
  // superseded is dropped the same way a poll from a disposed epoch is.
  private _pollGeneration = 0;

  constructor(
    private readonly _client: IComplianceCallClient,
    private readonly _directorStore: DirectorStore,
    private readonly _dataStorage: DataStorage,
    private readonly _analyticsManager: AnalyticsManager,
  ) {
    makeAutoObservable(this);
  }

  get customerMobile(): string | undefined {
    return this._directorStore.mainDirector?.mobile;
  }

  // The retry cooldown is much shorter than a call itself, so secondsLeft alone would unlock this
  // while a previous call is still IN_PROGRESS.
  get canRequestCall(): boolean {
    return this.secondsLeft <= 0 && !this.isRequesting && this.status !== ComplianceCallStatus.InProgress;
  }

  get countdown(): string {
    const minutes = Math.floor(this.secondsLeft / 60);
    const seconds = this.secondsLeft % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  // No limit on how many times this can happen — a failed call is always retryable.
  get isFailedCall(): boolean {
    return this.status === ComplianceCallStatus.Failed;
  }

  // Nothing more is expected from the customer on the call itself — either it isn't required at
  // all, or it already completed. Either way the next step is waiting for the compliance check.
  get isCallDone(): boolean {
    return !this.isRequired || this.status === ComplianceCallStatus.Completed;
  }

  async init(): Promise<void> {
    const epoch = ++this._epoch;
    this._refreshCountdown();
    this._startCountdown();

    try {
      const data = await this._client.getCallState();
      if (epoch !== this._epoch) {
        return;
      }

      runInAction(() => {
        this.status = data.status;
        this.isRequired = data.isRequired;
      });

      if (this.status === ComplianceCallStatus.InProgress) {
        this._startPolling();
      }
    } catch {
      // Initial state read failed — leave NOT_STARTED so the customer can request a call.
    }
  }

  async requestCall(): Promise<void> {
    // A second click while the POST is in flight would place a second call: the retry timestamp
    // is only written once the response lands.
    if (this.isRequesting) {
      return;
    }

    const epoch = this._epoch;

    runInAction(() => {
      this.isRequesting = true;
    });
    this._analyticsManager.event("compliance-call-requested");

    try {
      const data = await this._client.requestCall();
      if (epoch !== this._epoch) {
        return;
      }

      this._dataStorage.set(RETRY_AT_KEY, Date.now() + RETRY_WINDOW_MS);

      runInAction(() => {
        this.error = false;
        this.status = data.status;
        this.isRequired = data.isRequired;
      });

      this._refreshCountdown();
      this._startPolling();
    } catch {
      runInAction(() => {
        this.error = true;
      });
      this._analyticsManager.event("compliance-call-request-error");
    } finally {
      runInAction(() => {
        this.isRequesting = false;
      });
    }
  }

  dispose(): void {
    this._epoch += 1;
    this._clearPolling();
    this._clearCountdown();
  }

  private _startPolling(): void {
    this._clearPolling();
    const epoch = this._epoch;
    const generation = ++this._pollGeneration;
    void this._poll(epoch, generation);
    this._pollInterval = setInterval(() => {
      void this._poll(epoch, generation);
    }, POLL_INTERVAL_MS);
  }

  // No timeout here: a call stuck IN_PROGRESS is anna-kyb's own fallback to resolve (FAILED at the
  // latest after 30 minutes), so polling just keeps going until the status is terminal.
  private async _poll(epoch: number, generation: number): Promise<void> {
    try {
      const data = await this._client.getCallState();
      if (epoch !== this._epoch || generation !== this._pollGeneration || isTerminalComplianceCallStatus(this.status)) {
        // Disposed; superseded by a retry's own polling session (an old attempt's request can still
        // be in flight after the customer has already asked for a new call); or a slower request
        // from before the call settled landing after a faster one already reported the terminal
        // status. Either way the call can't un-settle, so this read is stale.
        return;
      }

      runInAction(() => {
        this.status = data.status;
        this.isRequired = data.isRequired;
      });
      this._analyticsManager.event("compliance-call-status", { status: data.status });

      if (isTerminalComplianceCallStatus(data.status)) {
        this._clearPolling();
      }
    } catch {
      // Transient read failure — keep polling.
    }
  }

  private _startCountdown(): void {
    this._clearCountdown();
    this._countdownInterval = setInterval(() => {
      this._refreshCountdown();
    }, 1000);
  }

  private _refreshCountdown(): void {
    const retryAt = this._dataStorage.get(RETRY_AT_KEY, Number) ?? 0;
    runInAction(() => {
      this.secondsLeft = Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
    });
  }

  private _clearPolling(): void {
    if (this._pollInterval) {
      clearInterval(this._pollInterval);
      this._pollInterval = undefined;
    }
  }

  private _clearCountdown(): void {
    if (this._countdownInterval) {
      clearInterval(this._countdownInterval);
      this._countdownInterval = undefined;
    }
  }
}
